import { useState, useEffect } from "react";
import { array } from "prop-types";
import cx from "classnames";
import throttle from "lodash.throttle";

const propTypes = {
  links: array.isRequired,
};

const JumpNavigation = (props) => {
  const { links } = props;

  // Creates a list of the anchors on the page
  const [anchors] = useState(
    links.map((link) => document.getElementById(link.href.slice(1))),
  );

  const [isScrolling, setIsScrolling] = useState(false);

  const [activeLinkHash, setActiveLinkHash] = useState(window.location.hash);

  const [activeLinkIndex, setActiveLinkIndex] = useState(() => {
    const hash = window.location.hash;

    if (hash) {
      const link = links.find((link) => {
        return link.href === hash;
      });
      return link ? link.index : -1;
    } else {
      return -1;
    }
  });

  // Update the hash value in the URL
  useEffect(() => {
    window.history.replaceState(null, null, activeLinkHash);
  }, [activeLinkHash]);

  // As the user is scrolling,
  // check to see if the active link needs to be updated.
  // Note: this also fires on jump nav click, but will just return, as it will hit the 'isScrolling' check.
  useEffect(() => {
    const throttleHandleScroll = throttle(() => {
      if (isScrolling) {
        return;
      }

      const index = [...anchors].reduce((index, anchor) => {
        const rect = anchor.getBoundingClientRect();
        return rect.top < window.innerHeight / 2 + 100 ? index + 1 : index;
      }, -1);

      if (index !== activeLinkIndex) {
        setActiveLinkIndex(index);

        if (anchors[index] !== undefined) {
          const id = anchors[index].id;
          setActiveLinkHash("#" + id);
        }
      }
    }, 100);

    window.addEventListener("scroll", throttleHandleScroll);

    return () => {
      window.removeEventListener("scroll", throttleHandleScroll);
    };
  }, [activeLinkIndex, isScrolling, anchors]);

  const handleLinkClick = (event, index, hash) => {
    event.preventDefault();
    setIsScrolling(true);
    document.body.classList.add("is-scrolling");

    // Set the new active index, and the new active hash.
    setActiveLinkIndex(index);
    setActiveLinkHash(hash);

    // Get the selected anchor.
    const scrollToAnchor = anchors[index];

    // Update part of the offset calculation based on whether
    // the global header is visible or not.
    const isGlobalHeaderVisible = !document
      .querySelector(".global-header")
      .classList.contains("scroll-top");

    let isMiniPageHeaderVisible = false;
    let miniPageHeader = document.querySelector(
      ".js-page-header__mini--exec-ed",
    );
    if (miniPageHeader) {
      isMiniPageHeaderVisible = document
        .querySelector(".js-page-header__mini--exec-ed")
        .classList.contains("is-visible");
    }

    // Force mini page header to scroll into view.
    // This fixes a bug where clicking on the first jump nav anchor link
    // causes the header to overlap page content.
    if (miniPageHeader && !isMiniPageHeaderVisible) {
      miniPageHeader.classList.add("is-visible");
    }

    let headerDifference;

    if (isGlobalHeaderVisible && isMiniPageHeaderVisible) {
      headerDifference = 325;
    } else if (!isGlobalHeaderVisible && isMiniPageHeaderVisible) {
      headerDifference = 225;
    } else if (isGlobalHeaderVisible && !isMiniPageHeaderVisible) {
      headerDifference = 200;
    } else {
      headerDifference = 100;
    }

    const offset =
      scrollToAnchor.getBoundingClientRect().top +
      window.scrollY -
      headerDifference;

    // Scroll to the location on the page, minus the offset, so nothing gets cut off.
    window.scrollTo({ top: offset, behavior: "smooth" });

    setTimeout(() => {
      // window.scrollTo({ top: offset });

      setIsScrolling(false);

      document.body.classList.remove("is-scrolling");
    }, 1000);
  };

  return (
    <nav className="jump-navigation" aria-label="Page section navigation">
      <ul className="jump-navigation__links">
        {links.map((link) => (
          <li
            className={cx("jump-navigation__link", {
              "jump-navigation__link--current": link.index === activeLinkIndex,
            })}
            key={link.index}
          >
            <a
              href={link.href}
              title={link.label}
              onClick={(e) => handleLinkClick(e, link.index, link.href)}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

JumpNavigation.propTypes = propTypes;
export default JumpNavigation;
